import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { api, handleApiCall } from '../services/api'
import NeurodiversityBadges from '../components/NeurodiversityBadges'
import PortalSwitcher from '../components/PortalSwitcher'
import './InstructorDashboard.css'

function InstructorDashboard() {
  const { user, profile, activePortal, availablePortals } = useAuth()
  const [courses, setCourses] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deletingCourseId, setDeletingCourseId] = useState(null)

  useEffect(() => {
    if (user) {
      fetchDashboardData()
    }
  }, [user])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      
      // Fetch instructor courses
      const coursesData = await handleApiCall(api.courses.getInstructorCourses, user.id)
      setCourses(coursesData || [])
      
      // Fetch enrollment statistics
      const enrollmentStats = await handleApiCall(api.enrollments.getStats, user.id)
      
      // Fetch all sessions for instructor's courses and count completed ones
      let completedSessionsCount = 0
      const now = new Date()
      
      for (const course of (coursesData || [])) {
        try {
          const sessionsData = await handleApiCall(api.sessions.getCourse, course.id, user.id)
          // Count sessions that have passed (session_date < now)
          const completed = (sessionsData || []).filter(s => new Date(s.session_date) < now).length
          completedSessionsCount += completed
        } catch (err) {
          console.error(`Error fetching sessions for course ${course.id}:`, err)
        }
      }
      
      setStats({
        ...enrollmentStats,
        completedSessions: completedSessionsCount
      })
      
      console.log('Dashboard data loaded:', { courses: coursesData, stats: enrollmentStats, completedSessions: completedSessionsCount })
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteCourse = async (courseId, courseTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${courseTitle}"? This action cannot be undone.`)) {
      return
    }

    try {
      setDeletingCourseId(courseId)
      await handleApiCall(api.courses.delete, courseId, user.id)
      
      // Remove from local state
      setCourses(courses.filter(c => c.id !== courseId))
      
      // Refresh stats
      const enrollmentStats = await handleApiCall(api.enrollments.getStats, user.id)
      setStats(enrollmentStats)
    } catch (error) {
      console.error('Error deleting course:', error)
      alert('Failed to delete course. Please try again.')
    } finally {
      setDeletingCourseId(null)
    }
  }

  return (
    <div className="instructor-dashboard">
      {/* Portal Switcher */}
      {availablePortals && availablePortals.length > 1 && (
        <div className="dashboard-portal-switcher">
          <PortalSwitcher 
            currentPortal={activePortal} 
            availablePortals={availablePortals} 
            compact={false} 
          />
        </div>
      )}
      
      <div className="dashboard-header">
        <div>
          <h1>Instructor Dashboard</h1>
          <div className="instructor-profile-summary">
            <p>Welcome back, {profile?.first_name || 'Instructor'}!</p>
            {profile?.neurodiversity_profile && profile.neurodiversity_profile.length > 0 && (
              <NeurodiversityBadges 
                profile={profile.neurodiversity_profile} 
                size="small" 
                showLabel={false}
              />
            )}
          </div>
        </div>
        <Link to="/teach/create-course" className="btn-primary">
          Create New Course
        </Link>
      </div>

      {/* Incomplete Profile Reminder */}
      {(!profile?.expertise || profile.expertise.length === 0) && (
        <div className="profile-reminder" style={{
          background: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.5rem' }}>📝</span>
            <p style={{ margin: 0, color: '#92400e' }}>
              Complete your instructor profile to help students learn more about your teaching specializations
            </p>
          </div>
          <Link 
            to="/profile?section=instructor" 
            className="btn-primary"
            style={{ whiteSpace: 'nowrap' }}
          >
            Complete Profile
          </Link>
        </div>
      )}

      {loading ? (
        <div className="loading-state" style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Loading dashboard...</p>
        </div>
      ) : error ? (
        <div className="error-state" style={{ textAlign: 'center', padding: '3rem' }}>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="btn-primary" style={{ marginTop: '1rem' }}>
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{courses.length}</div>
              <div className="stat-label">Total Courses</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats?.activeEnrollments || 0}</div>
              <div className="stat-label">Active Students</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats?.completedSessions || 0}</div>
              <div className="stat-label">Sessions Completed</div>
            </div>
          </div>

          <div className="dashboard-section">
            <h2>My Courses</h2>
            {courses.length === 0 ? (
              <div className="empty-state">
                <p>You haven't created any courses yet.</p>
                <Link to="/teach/create-course" className="btn-primary">
                  Create Your First Course
                </Link>
              </div>
            ) : (
              <div className="instructor-courses-list">
                {courses.map(course => {
                  const enrollmentCount = stats?.enrollmentsByCourse[course.title]?.active || 0
                  
                  // Truncate description to 150 characters
                  const truncatedDescription = course.description && course.description.length > 150
                    ? course.description.substring(0, 150) + '...'
                    : course.description
                  
                  return (
                    <div key={course.id} className="instructor-course-card">
                      <div className="course-details">
                        <h3>
                          <Link 
                            to={`/courses/${course.id}`}
                            className="course-title-link"
                          >
                            {course.title}
                          </Link>
                        </h3>
                        {truncatedDescription && (
                          <p className="course-description">{truncatedDescription}</p>
                        )}
                        <div className="course-meta">
                          <span className="enrollment-count">
                            👥 {enrollmentCount} active
                            {course.enrollment_limit ? ` / ${course.enrollment_limit}` : ''} students
                            {course.enrollment_limit && enrollmentCount >= course.enrollment_limit && (
                              <span className="full-indicator"> (FULL)</span>
                            )}
                          </span>
                          {!course.is_self_paced && course.session_duration && (
                            <span className="session-info">
                              📅 {course.session_duration} min sessions
                            </span>
                          )}
                          {course.is_self_paced && (
                            <span className="session-info">⏰ Self-paced</span>
                          )}
                          <span className={`status-badge ${course.is_published ? 'published' : 'draft'}`}>
                            {course.is_published ? '🟢 Published' : '📝 Draft'}
                          </span>
                        </div>
                      </div>
                      <div className="course-actions">
                        <Link to={`/teach/course/${course.id}/manage`} className="btn-primary">
                          Manage Course
                        </Link>
                        <div className="quick-actions">
                          <Link 
                            to={`/teach/course/${course.id}/manage?tab=chat`} 
                            className="quick-action-btn"
                            title="Chat"
                          >
                            💬
                          </Link>
                          {course.meeting_link ? (
                            <a 
                              href={course.meeting_link} 
                              target="_blank"
                              rel="noopener noreferrer"
                              className="quick-action-btn"
                              title="Join Meeting"
                            >
                              📹
                            </a>
                          ) : (
                            <Link 
                              to={`/teach/course/${course.id}/sessions`} 
                              className="quick-action-btn"
                              title="Join Meeting"
                            >
                              📹
                            </Link>
                          )}
                          <button
                            onClick={() => handleDeleteCourse(course.id, course.title)}
                            className="quick-action-btn delete-btn"
                            title="Delete Course"
                            disabled={deletingCourseId === course.id}
                          >
                            {deletingCourseId === course.id ? '⏳' : '✕'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default InstructorDashboard
